import type { ReactNode } from 'react';
import { ScenarioBanner } from '@/shared/ui/ScenarioBanner';
import { HologramHeader } from '../HologramHeader';
import { SystemFrame } from '../SystemFrame';
import { LuminariumBackdrop, useLuminariumReveal } from '@/shared/ui/organisms';
import { usePanelTilt, useViewportFitScale, useVirtualKeyboardInset } from '@/shared/lib/hooks';
import { useMediaQuery } from '../../../../shared/lib/useMediaQuery';
import { reducedMotion, mobile } from '@/shared/ui/theme';
import type { Transition } from '../../model/useStepTransition';
import { useFoldPanelIntoVeil } from '../../model/useFoldPanelIntoVeil';
import { srOnly } from '@/shared/ui/styles';
import * as s from './AuthSceneLayout.css';

export interface AuthSceneLayoutProps {
  /** Current entrance phase (0–5) */
  phase: number;
  /** Typed system text for HologramHeader */
  systemText: string;
  /** Current step transition state */
  transition: Transition;
  /** Whether SystemFrame should show glitch */
  frameGlitch: boolean;
  /** Step announcement text for screen readers */
  stepAnnouncement: string;
  /** Show warm (gold) ambient orb — Login uses 3 orbs, Register 2 */
  showWarmOrb?: boolean;
  /** Optional override for panel className */
  panelClassName?: string;
  /** Optional override for content container className */
  contentClassName?: string;
  /** Optional override for form area className */
  formAreaClassName?: string;
  /** Scene warp-out exit animation (auth → main transition) */
  sceneExiting?: boolean;
  /** Slot rendered above the panel (e.g. NeuralPath progress) */
  abovePanel?: ReactNode;
  /** 시나리오 배너 문장 — 단계별 서사. 없으면 시작 문장 */
  bannerMessage?: string;
  /** 신원이 확인된 뒤의 이름 — 환영 문장이 이 이름으로 부른다 */
  playerName?: string;
  /**
   * 성공 화면에서 켠다. 모달을 지금 모습 그대로 찍어 두었다가 넘길 때 베일에 싣고 광원장과 함께 접어 넘긴다.
   * 꺼져 있거나 찍지 못하면 모달은 지금처럼 따로 밝아지며 사라진다.
   */
  foldPanelIntoVeil?: boolean;
  /** SYS.GATE 줄 아래 슬롯 (접속 단계 레일) */
  rail?: ReactNode;
  /**
   * Declared (untransformed) natural height of the whole zoomed auth column, px.
   * = panel natural height + above-panel slot (~90) + gap (12). MUST be a
   * compile-time constant per step/flow — never a live DOM measurement — so
   * useViewportFitScale stays non-circular (no scrollbar↔scale jitter).
   * Defaults to the tallest step's height.
   */
  designHeight?: number;
  /** Content rendered inside the form area (step forms, error boxes, etc.) */
  children: ReactNode;
}

/**
 * Conservative single design height = the TALLEST step's natural (unzoomed)
 * column height, with a few px breathing room. Measured natural columns at
 * 1280×820: name 656 · method 780 · otp 917 · password 895 · success 687. The
 * constant MUST cover the tallest (otp ≈ 917) so computeFitScale DOWNSCALES tall
 * steps to fit the viewport instead of upscaling them past it (the design-doc
 * §5.3 overflow trap). Understating this (e.g. 702) makes the hook conclude there
 * is room to grow → zoom > 1 on every step → tall steps overflow → page scroll.
 * Declared CONSTANT only — never a live DOM measurement (keeps the hook
 * non-circular / no scrollbar↔scale jitter). Callers may override per step/flow.
 */
const DEFAULT_DESIGN_HEIGHT = 940;

/**
 * Shared auth scene layout.
 *
 * Provides: constellation background, ambient orbs, vignette,
 * tiltable panel with SystemFrame, ScenarioBanner, HologramHeader,
 * phase-based entrance, and step transition scan wipe.
 */
export function AuthSceneLayout({
  phase,
  systemText,
  transition,
  frameGlitch,
  stepAnnouncement,
  showWarmOrb = false,
  panelClassName,
  contentClassName,
  formAreaClassName,
  sceneExiting = false,
  abovePanel,
  bannerMessage,
  playerName,
  rail,
  foldPanelIntoVeil = false,
  designHeight = DEFAULT_DESIGN_HEIGHT,
  children,
}: AuthSceneLayoutProps) {
  const noMotion = useMediaQuery(reducedMotion);
  const isMobile = useMediaQuery(mobile);
  const noTilt = noMotion || isMobile;
  // 로그인 중엔 광원장이 설계도(와이어프레임)로 보이고, 성공하면 베일을 쥐어 돌려 넘겨 렌더로 연다
  useLuminariumReveal(sceneExiting, noMotion);
  const { panelRef, handleMouseMove, handleMouseEnter, handleMouseLeave } = usePanelTilt({
    enabled: !noTilt,
  });

  // 성공 화면의 모달을 미리 찍어 두고, 넘길 때 베일에 실어 광원장과 함께 접어 넘긴다
  const foldActive = foldPanelIntoVeil && !noMotion;
  const veilStage = useFoldPanelIntoVeil(panelRef, foldActive, sceneExiting);
  // 노트북 등 세로 짧은 화면에서 고정 기하 프레임을 뷰포트에 맞춰 연속 축소.
  // 모바일은 너비 기반으로 패널이 따로 리플로우하므로 비활성.
  // ref 는 zoom 이 적용되지 않는 씬(main)에 붙여 내부 콘텐츠 리사이즈를 관찰한다.
  const { ref: sceneRef, scale: fitScale } = useViewportFitScale<HTMLElement>({
    enabled: !isMobile,
    designHeight,
  });

  // 가상 키보드가 덮는 높이를 씬 하단 padding 으로 예약한다. 패널/가시영역 비율에
  // 따라 두 경로로 갈리고, 측정상 둘 다 입력창을 키보드 위로 올린다
  // (695px 뷰포트 · 336px 키보드 · 가시영역 359px 기준):
  //
  //  1) 패널이 가시영역보다 작으면(예: 300px) box-sizing:border-box 라 씬은 그대로
  //     100dvh 이고 content box 만 줄어든다 → authCenterColumn 의 margin:auto 가
  //     가시영역 기준으로 재중앙정렬(패널이 168px 위로, 스크롤 불필요).
  //  2) 패널이 더 크면(실제 minHeight 520px) content+padding 이 100dvh 를 넘겨 씬이
  //     856px 로 자라고 161px 스크롤 여지가 생긴다 → 패널 하단이 스크롤로 도달
  //     가능해져 브라우저 네이티브 scroll-into-view 가 살아난다.
  //
  // padding 이 없으면 씬이 정확히 100dvh 라 스크롤 여지가 0 이고, 키보드에 덮인
  // 입력창을 밀어올릴 수단이 아예 없다 — 이게 원래 버그였다.
  // 씬은 overflowY:auto (세로 넘치면 스크롤), overflowX:hidden. 배경 장식 오브는
  // sceneDecor(inset:0/overflow:hidden) 로 가둬 스크롤 영역을 늘리지 않으므로 in-flow
  // 콘텐츠만 씬을 밀어 키우고 (2) 경로가 성립한다.
  const keyboardInset = useVirtualKeyboardInset();

  // authSceneExiting 제거 — 배경 그리드가 메인과 연결되도록 씬 전체 fade out 안 함
  const sceneClass = s.authScene;
  // 베일에 실은 모달은 따로 사라지지 않는다 — 싣지 못했을 때만 지금처럼 밝아지며 사라진다
  const panelFades = sceneExiting && (!foldActive || veilStage === 'failed');
  const panelCls = `${panelClassName ?? s.authPanel}${panelFades ? ` ${s.authPanelExiting}` : ''}`;
  const contentCls = contentClassName ?? s.authContentContainer;
  const formAreaCls = formAreaClassName ?? s.authFormArea;
  const orbExitCls = sceneExiting ? ` ${s.authOrbExiting}` : '';

  return (
    <main
      id="main-content"
      ref={sceneRef}
      className={sceneClass}
      tabIndex={-1}
      style={keyboardInset > 0 ? { paddingBottom: `${keyboardInset}px` } : undefined}
    >
      {/* Screen reader announcements */}
      <div aria-live="polite" className={srOnly}>
        {stepAnnouncement}
      </div>

      {/* Vignette — exit 시 fade out */}
      <div
        className={s.vignetteOverlay}
        style={{ opacity: phase >= 1 && !sceneExiting ? 1 : 0, transition: 'opacity 600ms' }}
      />

      {/* Luminarium shell — 전환 시 유지 (메인 backdrop과 연결감) */}
      <LuminariumBackdrop phase={phase} intensity={sceneExiting ? 0.5 : 0.3} />

      {/* Scan line — exit 시 숨김 */}
      {phase >= 1 && !sceneExiting && <div className={s.globalScan} />}

      {/* Ambient orbs — transition in CSS (reduced-motion aware).
          sceneDecor 로 감싸 음수 오프셋 오브가 씬 스크롤 영역을 늘리지 않도록 클립한다. */}
      <div className={s.sceneDecor}>
        <div
          className={`${s.ambientOrb} ${s.ambientOrbBlue}${orbExitCls}`}
          style={{ opacity: phase >= 1 ? 1 : 0 }}
        />
        <div
          className={`${s.ambientOrb} ${s.ambientOrbCyan}${orbExitCls}`}
          style={{ opacity: phase >= 1 ? 1 : 0 }}
        />
        {showWarmOrb && (
          <div
            className={`${s.ambientOrb} ${s.ambientOrbWarm}${orbExitCls}`}
            style={{ opacity: phase >= 1 ? 1 : 0 }}
          />
        )}
      </div>

      {/* Center column: above-panel slot + panel */}
      <div className={s.authCenterColumn} style={fitScale === 1 ? undefined : { zoom: fitScale }}>
        {abovePanel}

        {/* Panel with 3D tilt — disabled on mobile / reduced-motion */}
        <div
          ref={panelRef}
          className={panelCls}
          style={{
            opacity: phase >= 1 && veilStage !== 'on' ? 1 : 0,
          }}
          onMouseMove={noTilt ? undefined : handleMouseMove}
          onMouseEnter={noTilt ? undefined : handleMouseEnter}
          onMouseLeave={noTilt ? undefined : handleMouseLeave}
        >
          <SystemFrame className={frameGlitch ? s.systemFrameGlitch : undefined} />

          <div className={contentCls}>
            <div className={s.innerGlow} />
            <div className={s.noiseOverlay} />

            {/* Scenario banner */}
            <div
              className={s.segmentBanner}
              style={{
                opacity: phase >= 1 ? 1 : 0,
              }}
            >
              <ScenarioBanner message={bannerMessage} />
            </div>

            {/* Hologram header with typing text */}
            <div
              className={s.segmentHeader}
              style={{
                opacity: phase >= 1 ? 1 : 0,
              }}
            >
              <HologramHeader systemText={systemText} playerName={playerName} rail={rail} />
            </div>

            {/* Form area with step transitions */}
            <div
              className={`${formAreaCls} ${s.segmentForm}`}
              style={{
                opacity: phase >= 1 ? 1 : 0,
                position: 'relative',
              }}
            >
              {transition === 'glitching' && <div className={s.transitionScanWipe} />}
              {children}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
